import { type SandpackFiles } from "@codesandbox/sandpack-react";
import { insertIntoBody } from "./insertIntoHead";
import { getEntryFile } from "./fileHelpers";
import { tailwindAnimateExtend } from "../templates/tailwind/config/tailwindcss-animate";
import tailwindAnimatePlugin from "../templates/tailwind/plugins/tailwindcss-animate.txt?raw";
import defaultTailwindConfig from "../templates/tailwind/config/default";
import { getFileContents } from "./getFileContents";

type TwConfig = typeof defaultTailwindConfig;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function objectToLiteral(obj: any): string {
  if (typeof obj === "function") {
    return obj.toString();
  }

  if (Array.isArray(obj)) {
    return `[${obj.map(objectToLiteral).join(", ")}]`;
  }

  if (typeof obj === "object" && obj !== null) {
    const props = Object.entries(obj)
      .map(
        ([key, value]) => `${JSON.stringify(key)}: ${objectToLiteral(value)}`,
      )
      .join(",\n");
    return `{\n${props}\n}`;
  }

  return JSON.stringify(obj);
}

export const adjustTailwindConfigForCDN = (files: SandpackFiles) => {
  const tailwindConfig = files["tailwind.config.js"];

  if (!tailwindConfig) {
    return;
  }

  const tailwindConfigContents = getFileContents(tailwindConfig);

  let modifiedTailwindConfigContents = tailwindConfigContents
    .replace("module.exports = {", "tailwind.config = {")
    .replace('const { fontFamily } = require("tailwindcss/defaultTheme")', "")
    .replace('plugins: [require("tailwindcss-animate")]', `plugins: []`)
    .replace(", ...fontFamily.sans", "");

  let config: TwConfig = defaultTailwindConfig;

  try {
    config = (() => {
      const tailwind = {
        config: {
          theme: {
            extend: {},
          },
        },
      };

      eval(modifiedTailwindConfigContents);

      return tailwind.config as TwConfig;
    })();
  } catch (error) {
    console.error("Error parsing tailwind config:", error);
  }

  // add tailwind animate to the config
  config.theme.extend = {
    ...config.theme.extend,
    ...tailwindAnimateExtend,
    keyframes: {
      ...(config.theme.extend?.keyframes ?? {}),
      ...tailwindAnimateExtend.keyframes,
    },
  };

  const parsedPlugin = eval(`(${tailwindAnimatePlugin})`);

  // @ts-expect-error - we know it's a valid plugin
  config.plugins = [parsedPlugin];

  modifiedTailwindConfigContents = `tailwind.config = ${objectToLiteral(
    config,
  )}`;

  if (typeof files["tailwind.config.js"] === "string") {
    files["tailwind.config.js"] = modifiedTailwindConfigContents;
  } else {
    files["tailwind.config.js"].code = modifiedTailwindConfigContents;
  }

  if ("src/index.css" in files) {
    const content = getFileContents(files["src/index.css"]);
    const modifiedIndexCssContents = content
      .replace(`@import 'tailwindcss/base';`, "")
      .replace(`@import 'tailwindcss/components';`, "")
      .replace(`@import 'tailwindcss/utilities';`, "");

    if (typeof files["src/index.css"] === "string") {
      files["src/index.css"] = modifiedIndexCssContents;
    } else {
      files["src/index.css"].code = modifiedIndexCssContents;
    }
  }
};

export const importTailwindFiles = (files: SandpackFiles) => {
  adjustTailwindConfigForCDN(files);

  const requiredFiles = ["tailwind.css", "tailwind.config.js"];
  const entryFileKey = getEntryFile(files);

  if (entryFileKey !== "index.html") {
    const imports = requiredFiles.reduce((acc, file) => {
      if (file in files) {
        return acc + `import "../${file}";\n`;
      }

      return acc;
    }, "");
    const entryFile = getFileContents(files[entryFileKey]);
    const updatedEntryFile = `${imports}${entryFile}`;
    files[entryFileKey] = updatedEntryFile;

    return;
  }

  const indexHtmlFile = getFileContents(files["index.html"]);
  const updatedContent = insertIntoBody(
    indexHtmlFile,
    `\n\t<script type="module" src="./tailwind.config.js"></script>`,
  );

  if (typeof files["index.html"] === "string") {
    files["index.html"] = updatedContent;
  } else {
    files["index.html"].code = updatedContent;
  }
};
